import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, Plus, Eye, Pencil, Trash2, Power, Ban, Users, Mail, Phone,
} from "lucide-react";

const STATUS_PILL = {
  ACTIVE: "bg-green-500/10 text-green-400 border-green-500/30",
  INACTIVE: "bg-red-500/10 text-red-400 border-red-500/30",
};

function StatusBadge({ status }) {
  return (
    <span
      data-testid={`disp-status-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${STATUS_PILL[status] || "bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700"}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {status?.toLowerCase()}
    </span>
  );
}

const EMPTY_FORM = { name: "", email: "", phone: "", position: "", notes: "", password: "" };

export default function Dispatchers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {id}=edit
  const [viewing, setViewing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/store/dispatchers", { params: { search: search || undefined } })
      .then(r => setRows(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing({});
  };
  const openEdit = (d) => {
    setForm({
      name: d.name || "",
      email: d.email || "",
      phone: d.phone || "",
      position: d.position || "",
      notes: d.notes || "",
      password: "",
    });
    setEditing(d);
  };
  const closeEdit = () => setEditing(null);

  const onSave = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone number are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/store/dispatchers/${editing.id}`, form);
        toast.success("Dispatcher updated.");
      } else {
        await api.post("/store/dispatchers", form);
        toast.success("Dispatcher added.");
      }
      closeEdit();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (d) => {
    const action = d.status === "ACTIVE" ? "deactivate" : "activate";
    try {
      await api.post(`/store/dispatchers/${d.id}/${action}`);
      toast.success(`Dispatcher ${action}d.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/store/dispatchers/${confirmDelete.id}`);
      toast.success("Dispatcher deleted.");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="dispatchers-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">
            / Store Package Dispatchers
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Package Dispatchers</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            Staff who help dispatch your packages at the store.
          </p>
        </div>
        <Button
          data-testid="add-dispatcher-btn"
          onClick={openNew}
          className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Dispatcher
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="dispatchers-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, phone, email…"
            className="pl-10 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus-visible:ring-cyan-400"
          />
        </div>
        <div className="text-xs font-mono-tech text-slate-500">
          {loading ? "Loading…" : `${rows.length} dispatcher${rows.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {rows.length === 0 && !loading ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-20 text-center bg-slate-100/30 dark:bg-slate-900/30">
          <Users className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="font-display text-lg text-slate-700 dark:text-slate-300 mb-1">No dispatchers yet</div>
          <div className="text-sm text-slate-500 mb-5">Add your first package dispatcher to keep your operation running smoothly.</div>
          <Button onClick={openNew} className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium">
            <Plus className="w-4 h-4 mr-2" /> Add Dispatcher
          </Button>
        </div>
      ) : (
        <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-slate-100/40 dark:bg-slate-900/40">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-slate-100/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Position</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Phone</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Added</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr
                    key={d.id}
                    data-testid={`dispatcher-row-${d.id}`}
                    className="border-b border-slate-200/60 dark:border-slate-800/60 hover:bg-slate-100/60 hover:dark:bg-slate-900/60 transition-colors"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-800 text-cyan-300 grid place-items-center font-display font-semibold">
                          {(d.name || "?")[0].toUpperCase()}
                        </div>
                        <div className="font-medium text-slate-900 dark:text-white">{d.name}</div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-700 dark:text-slate-300">{d.position || <span className="text-slate-500 dark:text-slate-600">—</span>}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{d.email || <span className="text-slate-500 dark:text-slate-600">—</span>}</td>
                    <td className="px-5 py-3 text-slate-700 dark:text-slate-300 font-mono-tech text-xs">{d.phone}</td>
                    <td className="px-5 py-3"><StatusBadge status={d.status} /></td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
                      {new Date(d.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          data-testid={`view-dispatcher-${d.id}`}
                          size="icon" variant="ghost"
                          title="View"
                          onClick={() => setViewing(d)}
                          className="h-8 w-8 text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`edit-dispatcher-${d.id}`}
                          size="icon" variant="ghost"
                          title="Edit"
                          onClick={() => openEdit(d)}
                          className="h-8 w-8 text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {d.status === "ACTIVE" ? (
                          <Button
                            data-testid={`deactivate-dispatcher-${d.id}`}
                            size="icon" variant="ghost"
                            title="Deactivate"
                            onClick={() => toggleStatus(d)}
                            className="h-8 w-8 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            data-testid={`activate-dispatcher-${d.id}`}
                            size="icon" variant="ghost"
                            title="Activate"
                            onClick={() => toggleStatus(d)}
                            className="h-8 w-8 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          >
                            <Power className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          data-testid={`delete-dispatcher-${d.id}`}
                          size="icon" variant="ghost"
                          title="Delete"
                          onClick={() => setConfirmDelete(d)}
                          className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing?.id ? "Edit Dispatcher" : "Add Dispatcher"}
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Dispatchers are visible only to your store.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Field label="Full Name">
              <Input
                data-testid="disp-form-name"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
              />
            </Field>
            <Field label="Phone Number">
              <Input
                data-testid="disp-form-phone"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
              />
            </Field>
            <Field label="Email (optional)">
              <Input
                data-testid="disp-form-email"
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
              />
            </Field>
            <Field label="Position / Role (optional)">
              <Input
                data-testid="disp-form-position"
                value={form.position}
                onChange={e => setForm({ ...form, position: e.target.value })}
                placeholder="e.g. Lead Dispatcher"
                className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
              />
            </Field>
            <Field label={editing?.id ? "New Password (leave blank to keep current)" : "Password (required for login)"}>
              <Input
                data-testid="disp-form-password"
                type="password"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder={editing?.id ? "••••••••" : "Set a password so they can log in"}
                className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Dispatchers log in at <span className="text-cyan-300">/login</span> with their email + password.
              </div>
            </Field>
            <Field label="Notes (optional)">
              <Textarea
                data-testid="disp-form-notes"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
              />
            </Field>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
              <Button
                variant="outline"
                onClick={closeEdit}
                className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
              >
                Cancel
              </Button>
              <Button
                data-testid="disp-form-save"
                onClick={onSave}
                disabled={saving}
                className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
              >
                {saving ? "Saving…" : (editing?.id ? "Update" : "Add Dispatcher")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Dispatcher Details</DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">Full record.</DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3 pb-3 border-b border-slate-200/60 dark:border-slate-800/60">
                <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-slate-800 text-cyan-300 grid place-items-center font-display font-bold text-lg">
                  {(viewing.name || "?")[0].toUpperCase()}
                </div>
                <div>
                  <div className="font-display font-semibold text-slate-900 dark:text-white">{viewing.name}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">{viewing.position || "Dispatcher"}</div>
                </div>
                <div className="ml-auto"><StatusBadge status={viewing.status} /></div>
              </div>
              <Row icon={Phone} label="Phone" value={viewing.phone} />
              <Row icon={Mail} label="Email" value={viewing.email || "—"} />
              {viewing.notes && (
                <div className="pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
                  <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-1">Notes</div>
                  <div className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{viewing.notes}</div>
                </div>
              )}
              <div className="text-[11px] text-slate-500 font-mono-tech pt-2">
                Added {new Date(viewing.created_at).toLocaleString()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-6">
          <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="font-display text-lg font-semibold mb-2">Delete this dispatcher?</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              <span className="text-slate-900 dark:text-white">{confirmDelete.name}</span> will be removed from your store.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                data-testid="disp-delete-cancel"
                variant="outline"
                onClick={() => setConfirmDelete(null)}
                className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
              >
                Cancel
              </Button>
              <Button
                data-testid="disp-delete-confirm"
                onClick={onDelete}
                className="bg-red-500 text-slate-900 dark:text-white hover:bg-red-400"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-200/60 dark:border-slate-800/60 last:border-0">
      <Icon className="w-4 h-4 text-cyan-400 mt-0.5" />
      <div className="flex-1">
        <div className="text-[11px] font-mono-tech uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-slate-900 dark:text-white">{value}</div>
      </div>
    </div>
  );
}
